import type { Permission } from "../api/types.ts";
import { tr } from "../i18n/index.tsx";

/** Permission matrix rows (page) → actions, in display order. Unknown permissions go to "Other". */
export const PERMISSION_GROUPS: { page: string; prefixes: string[] }[] = [
  { page: "Overview", prefixes: ["overview"] },
  { page: "Deposits", prefixes: ["deposits"] },
  { page: "Payouts", prefixes: ["payouts"] },
  { page: "Statement", prefixes: ["statement"] },
  { page: "Vault", prefixes: ["vault"] },
  { page: "API keys", prefixes: ["api_keys"] },
  { page: "Webhook", prefixes: ["webhook"] },
  { page: "Team", prefixes: ["team"] },
  { page: "Roles", prefixes: ["roles"] },
  { page: "Activity", prefixes: ["activity"] },
  { page: "Settings", prefixes: ["settings", "console_ip"] },
];

/** Column label for a permission ("payouts:approve" → "Approve"), in the console language. */
export function permissionLabel(p: string): string {
  const c = tr().common;
  if (p === "console_ip:edit") return c.permConsoleIp;
  return (c.permActions as Record<string, string>)[p.split(":")[1] ?? ""] ?? p;
}

/** What each permission allows, for tooltips (read from the active dictionary). */
export const PERMISSION_HELP = new Proxy({} as Partial<Record<Permission, string>>, {
  get: (_, k) =>
    typeof k === "string"
      ? ((tr().common.permHelp as Record<string, string>)[k] ?? (tr().vault.permHelp as Record<string, string>)[k])
      : undefined,
});

export function groupPermissions(all: string[]) {
  const used = new Set<string>();
  const rows = PERMISSION_GROUPS.map((g) => {
    const perms = all.filter((p) => g.prefixes.includes(p.split(":")[0] ?? ""));
    perms.forEach((p) => used.add(p));
    const pages: Record<string, string> = { ...(tr().common.permPages as Record<string, string>), Vault: tr().vault.permPage };
    return { page: pages[g.page] ?? g.page, perms };
  }).filter((r) => r.perms.length);
  const other = all.filter((p) => !used.has(p));
  if (other.length) rows.push({ page: tr().common.permPages.Other, perms: other });
  return rows;
}
